import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import Placeholder from "@tiptap/extension-placeholder";
import Heading from "@tiptap/extension-heading";
import Bold from "@tiptap/extension-bold";
import History from "@tiptap/extension-history";
import OrderedList from "@tiptap/extension-ordered-list";
import BulletList from "@tiptap/extension-bullet-list";
import ListItem from "@tiptap/extension-list-item";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import Document from "@tiptap/extension-document";
import Image from "@tiptap/extension-image";
import Wrapper, { WrapperProps } from "src/components/field/utils/Wrapper";
import withTranslation, {
	Translation,
} from "src/components/hoc/withTranslation";
import Header from "src/components/field/fields/Editor/Header";

interface EditorProps extends Translation {
	wrapper?: WrapperProps;
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	options?: {
		autoFocus?: boolean;
	};
}

const Editor = ({
	t,
	wrapper,
	value,
	onChange,
	placeholder: _placeholder,
	options,
}: EditorProps) => {
	const placeholder = _placeholder || t("placeholder");
	const editor = useEditor({
		extensions: [
			Document,
			BulletList,
			OrderedList,
			ListItem,
			Bold,
			History.configure({
				depth: 10,
			}),
			Heading.configure({
				levels: [2, 3],
			}),
			Paragraph,
			Text,
			Placeholder.configure({
				placeholder: ({ node }) => {
					return placeholder;
				},
			}),
			Image.configure({}),
		],
		onUpdate: ({ editor }) => onChange(editor.getHTML()),
		content: value,
	});

	useEffect(() => {
		if (options?.autoFocus) {
			editor?.commands.focus("end");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Wrapper {...wrapper}>
			<div
				className={
					"relative rounded-md  min-h-[300px] flex flex-col flex-1"
				}
			>
				{editor && <Header {...{ editor }} />}
				<div
					className="relative cursor-text pt-4 p-1"
					onClick={() =>
						!value
							? editor?.commands.focus("end")
							: editor?.commands.focus()
					}
				>
					<EditorContent
						className="cursor-text"
						placeholder={placeholder || t("placeholder")}
						style={{ minHeight: "300px" }}
						{...{ editor }}
					/>
				</div>
			</div>
		</Wrapper>
	);
};

Editor.locale = {
	nl: {
		placeholder: "Begin met typen...",
	},
	en: {
		placeholder: "Start typing",
	},
};

export default withTranslation(Editor);
